import { keepPreviousData } from "@tanstack/react-query";
import noop from "lodash/noop";
import some from "lodash/some";
import Chat from "PFApp/components/chat/chat";
import { LOADING, NORMAL } from "PFApp/components/chat/chat_states";
import { usePusherEvent } from "PFCore/base/pusher";
import { useDebounce } from "PFCore/helpers/use_debounce";
import { useChatConversation } from "PFCore/hooks/queries/chat/use_chat_conversation";
import { useChatInvalidate } from "PFCore/hooks/queries/chat/use_chat_invalidate";
import { useChatMessages } from "PFCore/hooks/queries/chat/use_chat_messages";
import { useChatParticipants } from "PFCore/hooks/queries/chat/use_chat_participants";
import { useCurrentProfile } from "PFCore/hooks/queries/profile/use_current_profile";
import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useState } from "react";

const ChatController = ({
  style,
  conversationId,
  selected,
  expanded,
  expandable,
  emptyMessage = "No messages",
  isBelow,
  minimized,
  onSelect = noop,
  onMinimize = noop,
  onExpand = noop,
  onUnMinimize = noop,
  onClose = noop,
  onClearNotifications = noop
}) => {
  const { data: currentProfile } = useCurrentProfile();

  const [chatState, setChatState] = useState(LOADING);
  const [flashTitle, setFlashTitle] = useState(false);
  const [lastReceivedMessage, setLastReceivedMessage] = useState(null);

  const { invalidateAllConversations } = useChatInvalidate();

  const { data: conversation, refetch: refetchConversation } = useChatConversation(conversationId, {
    gcTime: 0 // we need to refetch conversation each time chat is opened to get fresh data
  });

  const { data: participants, refetch: refetchChatParticipants } = useChatParticipants({
    conversationId: conversationId,
    perPage: 5
  });
  const { data: currentParticipants = [] } = useChatParticipants({
    conversationId: conversationId,
    profileId: currentProfile.id,
    perPage: 1
  });

  const {
    data: messages,
    isFetching,
    isFetched,
    isFetchingNextPage: isFetchingPreviousMessages,
    refetch: refetchMessages,
    fetchNextPage
  } = useChatMessages(
    {
      conversationId,
      targetId: conversation?.targetId,
      targetType: conversation?.targetType
    },
    {
      enabled: !!conversation,
      placeholderData: keepPreviousData,
      gcTime: 0 // we need to refetch messages each time chat is opened to get fresh data
    }
  );

  const allParticipants = useMemo(
    () => (currentParticipants.length ? [currentParticipants[0], ...participants] : participants),
    [currentParticipants, participants]
  );

  const unreadCount = conversation?.unreadCount ?? 0;

  // after chat window is opened and messages are fetched
  // we need to clear unread notifications and refresh conversations
  // because when message are fetched they are marked as read, and related conversation is upaded
  useEffect(() => {
    if (isFetched) {
      if (unreadCount > 0) {
        onClearNotifications();
        invalidateAllConversations();
        refetchConversation();
      }
      setChatState(NORMAL);
    }
  }, [isFetched]); // eslint-disable-line react-hooks/exhaustive-deps

  // we do not fetch messages when chat is minimized,
  // when chat is unminimized we need to refresh messages and conversation
  const handleUnMinimize = useCallback(async () => {
    onUnMinimize?.(conversation.id);
    if (unreadCount > 0) {
      await refetchMessages();
      await refetchConversation();
      onClearNotifications();
      invalidateAllConversations();
      setChatState(NORMAL);
    }
  }, [
    conversation,
    onUnMinimize,
    unreadCount,
    refetchMessages,
    refetchConversation,
    onClearNotifications,
    invalidateAllConversations
  ]);

  useEffect(() => {
    setChatState(isFetching ? LOADING : NORMAL);
  }, [isFetching]);

  const handlePusherNewMessage = (args) => {
    setLastReceivedMessage(args);
    // On activity page we do not show newly created conversations (without any message)
    // We need to refresh data when first message is received
    if (conversation.target && conversation.createdAt === conversation.lastActivityAt) {
      invalidateAllConversations();
      refetchConversation();
    }
  };

  usePusherEvent(`private-conversation-${conversationId}`, "message", handlePusherNewMessage);
  usePusherEvent(`private-conversation-${conversationId}`, "change", refetchChatParticipants);

  // PusherWrapper is binding the lexical scope so we need to set the lastReceivedMessage in state
  // In order to keep in sync with query update we need to debounce the flashTitle update and refetch
  useEffect(
    useDebounce(() => {
      const { pages } = messages || {};
      if (lastReceivedMessage && !some(pages[0]?.entries, { id: lastReceivedMessage.id })) {
        // if chat is minimized we just want to increase unread count (refetch conversation)
        minimized ? refetchConversation() : refetchMessages();

        setFlashTitle(true);
        setTimeout(() => {
          setFlashTitle(false);
        }, 3000);
      }

      setLastReceivedMessage(null);
    }, 200),
    [lastReceivedMessage]
  );

  const handleLoadPreviousMessages = useCallback(() => {
    fetchNextPage();
  }, [fetchNextPage]);

  return (
    <Chat
      style={style}
      conversationId={conversationId}
      selected={selected}
      expanded={expanded}
      expandable={expandable}
      isBelow={isBelow}
      minimized={minimized}
      emptyMessage={emptyMessage}
      onClose={onClose}
      onExpand={onExpand}
      onLoadPreviousMessages={handleLoadPreviousMessages}
      onMinimize={onMinimize}
      onSelect={onSelect}
      onUnMinimize={handleUnMinimize}
      participants={allParticipants}
      messages={messages}
      conversation={conversation || {}}
      chatState={chatState}
      setChatState={setChatState}
      currentProfile={currentProfile}
      flashTitle={flashTitle}
      isFetchingPreviousMessages={isFetchingPreviousMessages}
    />
  );
};

ChatController.propTypes = {
  style: PropTypes.object,
  conversation: PropTypes.any,
  conversationId: PropTypes.number,
  selected: PropTypes.bool,
  expanded: PropTypes.bool,
  expandable: PropTypes.bool,
  emptyMessage: PropTypes.string,
  isBelow: PropTypes.bool,
  minimized: PropTypes.bool,

  onSelect: PropTypes.func.isRequired,
  onMinimize: PropTypes.func,
  onExpand: PropTypes.func,
  onUnMinimize: PropTypes.func,
  onClose: PropTypes.func,
  onClearNotifications: PropTypes.func
};

export default ChatController;
